import { z } from 'zod';
import { normalizePlate, compactPlate, type PlateKind } from './plate.js';
import { toE164 } from './phone.js';

/**
 * Import des clients et véhicules depuis un fichier CSV (08/10/2026). Fonctions pures partagées : l'interface
 * décode et lit le fichier pour proposer la correspondance des colonnes, l'API refait toute l'analyse
 * (jamais de confiance dans le navigateur). Normalisation identique aux formulaires : `normalizePlate`,
 * `toE164` (pays du garage), e-mail en minuscules, nom et prénom séparés.
 */
export const CUSTOMER_IMPORT_LIMITS = {
  /** Lignes de données au plus (en-tête non comprise). */
  maxRows: 5000,
  /** Taille du texte décodé (caractères). */
  maxChars: 3 * 1024 * 1024,
  /** Lignes montrées dans l'aperçu. */
  previewRows: 20,
  /** Colonnes au plus. */
  maxColumns: 60,
} as const;

export const IMPORT_FIELDS = [
  'lastName',
  'firstName',
  'fullName',
  'companyName',
  'phone',
  'email',
  'address',
  'plate',
  'make',
  'model',
  'vin',
  'firstRegistration',
  'mileage',
  'notes',
] as const;
export const ImportField = z.enum(IMPORT_FIELDS);
export type ImportField = z.infer<typeof ImportField>;

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  lastName: 'Nom',
  firstName: 'Prénom',
  fullName: 'Nom complet (prénom et nom)',
  companyName: 'Raison sociale',
  phone: 'Téléphone',
  email: 'E-mail',
  address: 'Adresse',
  plate: 'Plaque',
  make: 'Marque',
  model: 'Modèle',
  vin: 'VIN',
  firstRegistration: 'Date de 1re mise en circulation',
  mileage: 'Kilométrage',
  notes: 'Remarques',
};

/** En-têtes reconnus (comparés sans accents, casse ni ponctuation), du plus précis au plus large. */
const SYNONYMS: Record<ImportField, string[]> = {
  firstRegistration: ['date de 1re mise en circulation', 'date 1re mise en circulation', 'mise en circulation', 'date mec', 'mec', '1re mec', 'date de premiere immatriculation', 'premiere immatriculation', 'date immatriculation', 'first registration'],
  companyName: ['raison sociale', 'societe', 'entreprise', 'company', 'denomination'],
  fullName: ['nom complet', 'nom et prenom', 'nom prenom', 'prenom nom', 'client', 'full name'],
  firstName: ['prenom', 'first name', 'firstname'],
  lastName: ['nom de famille', 'nom', 'last name', 'lastname', 'name'],
  phone: ['telephone portable', 'portable', 'mobile', 'telephone', 'tel', 'gsm', 'phone'],
  email: ['adresse e mail', 'adresse email', 'e mail', 'email', 'courriel', 'mail'],
  address: ['adresse postale', 'adresse', 'address', 'rue'],
  plate: ['immatriculation', 'plaque', 'immat', 'plate', 'numero d immatriculation'],
  make: ['marque', 'constructeur', 'make', 'brand'],
  model: ['modele', 'model', 'vehicule'],
  vin: ['vin', 'numero de serie', 'numero vin', 'chassis'],
  mileage: ['kilometrage', 'km', 'kilometres', 'compteur', 'mileage'],
  notes: ['remarques', 'remarque', 'observations', 'commentaire', 'commentaires', 'notes', 'note'],
};

export type ImportMapping = Partial<Record<ImportField, number>>;
export const ImportMappingSchema = z.strictObject(
  Object.fromEntries(IMPORT_FIELDS.map((f) => [f, z.number().int().min(0).max(CUSTOMER_IMPORT_LIMITS.maxColumns - 1).optional()])) as Record<
    ImportField,
    z.ZodOptional<z.ZodNumber>
  >,
);

// ─── Lecture du fichier ──────────────────────────────────────────────────────

/** Décode le fichier : UTF-8 (BOM retiré) si valide, sinon Windows-1252 (export Excel français). */
export function decodeCsvBytes(bytes: Uint8Array): { text: string; encoding: 'utf-8' | 'windows-1252' } {
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return { text: text.replace(/^\uFEFF/, ''), encoding: 'utf-8' };
  } catch {
    return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'windows-1252' };
  }
}

export type CsvDelimiter = ';' | ',' | '\t';

/** Séparateur le plus fréquent hors guillemets sur la première ligne (`;` en cas d'égalité). */
export function detectDelimiter(text: string): CsvDelimiter {
  const counts: Record<CsvDelimiter, number> = { ';': 0, ',': 0, '\t': 0 };
  let quoted = false;
  for (const ch of text) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && (ch === '\n' || ch === '\r')) break;
    else if (!quoted && ch in counts) counts[ch as CsvDelimiter] += 1;
  }
  const best = (Object.keys(counts) as CsvDelimiter[]).reduce((a, b) => (counts[b] > counts[a] ? b : a), ';');
  return counts[best] === 0 ? ';' : best;
}

/** CSV (RFC 4180) : guillemets doublés, retours à la ligne dans une cellule, CRLF. Lignes vides ignorées. */
export function parseCsv(text: string, delimiter: CsvDelimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const push = () => {
    row.push(cell);
    cell = '';
  };
  const end = () => {
    push();
    if (row.some((c) => c.trim() !== '')) rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] as string;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"' && cell.trim() === '') {
      quoted = true;
      cell = '';
    } else if (ch === delimiter) push();
    else if (ch === '\n') end();
    else if (ch === '\r') {
      if (text[i + 1] === '\n') i += 1;
      end();
    } else cell += ch;
  }
  if (cell !== '' || row.length > 0) end();
  return rows;
}

const normHeader = (h: string) =>
  h
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Correspondance proposée : chaque en-tête reconnu, une colonne par champ au plus. */
export function suggestMapping(headers: readonly string[]): ImportMapping {
  const mapping: ImportMapping = {};
  const used = new Set<number>();
  const normalized = headers.map(normHeader);
  // Égalité stricte d'abord, puis en-tête qui contient un synonyme en mots entiers (« Téléphone portable », « N° VIN »).
  for (const exact of [true, false]) {
    for (const field of Object.keys(SYNONYMS) as ImportField[]) {
      if (mapping[field] !== undefined) continue;
      for (const syn of SYNONYMS[field]) {
        const idx = normalized.findIndex((h, i) => !used.has(i) && (exact ? h === syn : ` ${h} `.includes(` ${syn} `)));
        if (idx >= 0) {
          mapping[field] = idx;
          used.add(idx);
          break;
        }
      }
    }
  }
  return mapping;
}

/** Texte, en-têtes, lignes : erreur lisible si le fichier est vide ou dépasse les plafonds. */
export function readImportFile(
  text: string,
  delimiter?: CsvDelimiter,
): { ok: true; delimiter: CsvDelimiter; headers: string[]; rows: string[][] } | { ok: false; message: string } {
  if (text.length > CUSTOMER_IMPORT_LIMITS.maxChars) return { ok: false, message: 'Fichier trop volumineux (3 Mo au plus).' };
  const sep = delimiter ?? detectDelimiter(text);
  const all = parseCsv(text.replace(/^\uFEFF/, ''), sep);
  const [headers, ...rows] = all;
  if (!headers || rows.length === 0) return { ok: false, message: 'Le fichier ne contient aucune ligne de données sous l’en-tête.' };
  if (headers.length > CUSTOMER_IMPORT_LIMITS.maxColumns) return { ok: false, message: `Trop de colonnes (${CUSTOMER_IMPORT_LIMITS.maxColumns} au plus).` };
  if (rows.length > CUSTOMER_IMPORT_LIMITS.maxRows) {
    return { ok: false, message: `Trop de lignes : ${rows.length} (${CUSTOMER_IMPORT_LIMITS.maxRows} au plus). Découpez le fichier.` };
  }
  return { ok: true, delimiter: sep, headers: headers.map((h) => h.trim()), rows };
}

// ─── Analyse d'une ligne ─────────────────────────────────────────────────────

export interface ImportedCustomer {
  kind: 'person' | 'company';
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  /** Adresse, contact d'une société et remarques : regroupés dans la remarque interne (aucune colonne dédiée). */
  notes: string | null;
}
export interface ImportedVehicle {
  plate: string;
  plateKind: PlateKind;
  make: string;
  model: string;
  vin: string | null;
  /** Date de 1re mise en circulation et kilométrage : dans la remarque interne du véhicule. */
  notes: string | null;
}
export type AnalyzedRow =
  | { ok: true; customer: ImportedCustomer; vehicle: ImportedVehicle | null }
  | { ok: false; errors: string[] };

export interface ImportOptions {
  /** Pays du garage (indicatif par défaut des téléphones). */
  countryCode: string;
  /** Plaque non française acceptée comme plaque étrangère ou spéciale (sinon erreur). */
  foreignPlates: boolean;
}

const EMAIL = z.email().max(254);
const VIN = /^[A-HJ-NPR-Z0-9]{11,17}$/;
const MAX_TEXT = 200;
const NOTES_MAX = 2000;

/** Date civile : JJ/MM/AAAA, JJ-MM-AAAA, JJ.MM.AAAA ou AAAA-MM-JJ → « JJ/MM/AAAA », sinon `null`. */
export function parseImportDate(raw: string): string | null {
  const v = raw.trim();
  let d: number, m: number, y: number;
  const fr = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(v);
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(v);
  if (fr) [d, m, y] = [Number(fr[1]), Number(fr[2]), Number(fr[3])];
  else if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (y < 1900 || y > 2100 || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/** Kilométrage : chiffres avec espaces ou points de milliers, « km » facultatif ; 0 à 5 000 000. */
export function parseImportMileage(raw: string): number | null {
  const v = raw.trim().toLowerCase().replace(/\s*km$/, '').replace(/[\s\u00a0\u202f.]/g, '');
  if (!/^\d{1,7}$/.test(v)) return null;
  const n = Number(v);
  return n <= 5_000_000 ? n : null;
}

/** « DUPONT Jean » → nom DUPONT ; « Jean Dupont » → prénom Jean, nom Dupont. */
export function splitFullName(raw: string): { firstName: string | null; lastName: string | null } {
  const words = raw.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { firstName: null, lastName: null };
  if (words.length === 1) return { firstName: null, lastName: words[0] as string };
  const upper = words.filter((w) => w.length > 1 && w === w.toUpperCase() && /\p{L}/u.test(w));
  if (upper.length > 0 && upper.length < words.length) {
    return { lastName: upper.join(' '), firstName: words.filter((w) => !upper.includes(w)).join(' ') };
  }
  return { firstName: words[0] as string, lastName: words.slice(1).join(' ') };
}

const joinNotes = (parts: (string | null)[]) => {
  const text = parts.filter((p): p is string => Boolean(p)).join('\n');
  return text === '' ? null : text;
};

/** Valide et normalise une ligne selon la correspondance. Toutes les erreurs de la ligne sont rendues. */
export function analyzeImportRow(cells: readonly string[], mapping: ImportMapping, opts: ImportOptions): AnalyzedRow {
  const get = (f: ImportField) => {
    const i = mapping[f];
    return i === undefined ? '' : (cells[i] ?? '').trim();
  };
  const errors: string[] = [];
  const short = (f: ImportField) => {
    const v = get(f).replace(/\s+/g, ' ');
    if (v.length > MAX_TEXT) errors.push(`${IMPORT_FIELD_LABELS[f]} trop long (${MAX_TEXT} caractères au plus)`);
    return v === '' ? null : v.slice(0, MAX_TEXT);
  };

  let firstName = short('firstName');
  let lastName = short('lastName');
  if (!lastName && !firstName && get('fullName')) {
    const split = splitFullName(get('fullName'));
    firstName = split.firstName?.slice(0, MAX_TEXT) ?? null;
    lastName = split.lastName?.slice(0, MAX_TEXT) ?? null;
  }
  const companyName = short('companyName');
  const kind = companyName ? 'company' : 'person';
  if (kind === 'person' && !lastName) errors.push('Nom ou raison sociale manquant');

  const rawEmail = get('email');
  let email: string | null = null;
  if (rawEmail) {
    if (EMAIL.safeParse(rawEmail).success) email = rawEmail.toLowerCase();
    else errors.push('E-mail invalide');
  }
  const rawPhone = get('phone');
  let phone: string | null = null;
  if (rawPhone) {
    phone = toE164(opts.countryCode, rawPhone);
    if (!phone) errors.push('Téléphone invalide');
  }

  const address = get('address').replace(/\s+/g, ' ');
  const contact = kind === 'company' ? [firstName, lastName].filter(Boolean).join(' ') : '';
  const customerNotes = joinNotes([address ? `Adresse : ${address}` : null, contact ? `Contact : ${contact}` : null, get('notes') || null]);
  if (customerNotes && customerNotes.length > NOTES_MAX) errors.push(`Remarques trop longues (${NOTES_MAX} caractères au plus)`);

  // Véhicule : facultatif, mais une ligne qui en parle doit donner plaque, marque et modèle.
  const rawPlate = get('plate');
  const make = short('make');
  const model = short('model');
  const rawVin = get('vin').replace(/\s+/g, '').toUpperCase();
  const reg = get('firstRegistration');
  const km = get('mileage');
  let vehicle: ImportedVehicle | null = null;
  if (rawPlate || make || model || rawVin || reg || km) {
    let plate: string | null = null;
    let plateKind: PlateKind = 'fr';
    if (!rawPlate) errors.push('Plaque manquante pour le véhicule');
    else {
      const fr = normalizePlate('fr', rawPlate);
      if (fr.ok) plate = fr.value;
      else if (opts.foreignPlates) {
        const other = normalizePlate('other', rawPlate);
        if (other.ok) [plate, plateKind] = [other.value, 'other'];
        else errors.push('Plaque invalide');
      } else errors.push('Plaque invalide (formats AA-123-AA ou 123 ABC 45)');
    }
    if (!make || !model) errors.push('Marque et modèle requis pour le véhicule');
    if (rawVin && !VIN.test(rawVin)) errors.push('VIN invalide');
    let regText: string | null = null;
    if (reg) {
      regText = parseImportDate(reg);
      if (!regText) errors.push('Date de 1re mise en circulation invalide (JJ/MM/AAAA)');
    }
    let mileage: number | null = null;
    if (km) {
      mileage = parseImportMileage(km);
      if (mileage === null) errors.push('Kilométrage invalide');
    }
    if (plate && make && model) {
      vehicle = {
        plate,
        plateKind,
        make,
        model,
        vin: rawVin || null,
        notes: joinNotes([
          regText ? `1re mise en circulation : ${regText}` : null,
          mileage !== null ? `Kilométrage à l’import : ${mileage.toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ')} km` : null,
        ]),
      };
    }
  }
  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    customer: { kind, firstName: kind === 'company' ? null : firstName, lastName: kind === 'company' ? null : lastName, companyName, email, phone, notes: customerNotes },
    vehicle,
  };
}

/** Clé d'unicité d'une plaque dans un garage. */
export const importPlateKey = (plate: string) => compactPlate(plate);

// ─── Fichiers produits ───────────────────────────────────────────────────────

/** Cellule CSV : guillemets si besoin ; `=`, `+`, `-`, `@` en tête neutralisés (formules du tableur). */
export function csvCell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[";\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Fichier CSV des lignes en erreur : en-tête d'origine + « Ligne » + « Erreurs » ; `;`, BOM UTF-8. */
export function buildImportErrorsCsv(headers: readonly string[], rows: readonly { line: number; cells: readonly string[]; errors: readonly string[] }[]): string {
  const lines = [['Ligne', 'Erreurs', ...headers], ...rows.map((r) => [String(r.line), r.errors.join(' ; '), ...r.cells])];
  return `\uFEFF${lines.map((l) => l.map(csvCell).join(';')).join('\r\n')}\r\n`;
}

/** Modèle téléchargeable depuis l'écran d'import. */
export const CUSTOMER_IMPORT_TEMPLATE_CSV =
  '\uFEFF' +
  [
    'Nom;Prénom;Raison sociale;Téléphone;E-mail;Adresse;Plaque;Marque;Modèle;VIN;Date de 1re mise en circulation;Kilométrage;Remarques',
    'Durand;Claire;;06 12 34 56 78;claire.durand@example.fr;12 rue des Lilas 69003 Lyon;AB-123-CD;Porsche;911 Carrera;WP0ZZZ99ZTS392124;15/03/2018;45 000;Cliente fidèle',
    ';;Transports Martin;04 78 00 00 00;contact@transports-martin.example.fr;;EF-456-GH;Renault;Master;;;120000;',
  ].join('\r\n') +
  '\r\n';
